import styled from "@emotion/styled"
import { AnimatePresence } from "framer-motion"
import { DateTime, Info } from "luxon"
import { createPortal } from "react-dom"
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useDictionary } from "contexts/Dictionary"
import { useMoonphaseCalendar } from "contexts/MoonphaseCalendar"
import { convertToIndianNumerals } from "./context"
import getMediaQuery from "css/breakpoints"
import { bold, headline40, light, visuallyHidden, legend110 } from "css/text"
import { useMoonCalendar } from "./context"
import { Icon } from "components/icon/Icon"
import ModalContainer, { moonphaseModal } from "components/modal/ModalContainer"
import buildMonthCalendar from "./buildMonthCalendar"
import MoonphaseInstructions from "./MoonphaseInstructions"
import MoonSprite from "./MoonSprite"
import { useConsole } from "contexts/Console"

const CalendarModal = styled(ModalContainer)`
  && {
    /* background-color: rgb(15 39 114 / 0.4); */
    background-color: rgb(0 43 93 / 0.4);
  }
`

const Panel = styled.section`
  backdrop-filter: blur(20px);
  /* background: rgb(186 188 233 / 0.3); */
  background: rgb(56 91 142 / 0.3);
  border-radius: 20px;
  color: rgb(var(--pure-white));
  inline-size: min(700px, calc(100vw - 32px));
  margin-inline: auto;
  overflow: clip;
  padding-block-end: 20px;

  html.prefers-contrast & {
    color: black;
    background: white;
  }
`

const Header = styled.header`
  align-items: center;
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) 44px;
  padding: 30px;

  ${getMediaQuery("s", { max: true })} {
    padding: 28px 20px;
  }
`

const Title = styled.h2`
  ${headline40}
  grid-column: 2;
  text-align: center;
`

const IconButton = styled.button`
  align-items: center;
  appearance: none;
  background: none;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  inline-size: 44px;
  justify-content: center;
  min-block-size: 44px;
  padding: 0;

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }

  &:disabled {
    cursor: default;
    opacity: 0.35;
  }
`

const CloseButton = styled(IconButton)`
  grid-column: 3;
  justify-self: end;
`

const HemisphereControls = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  margin-block-end: 30px;

  ${getMediaQuery("s", { max: true })} {
    gap: 12px;
  }
`

const HemisphereButton = styled.button`
  appearance: none;
  background: none;
  border: 0;
  color: inherit;
  cursor: pointer;
  font-family: inherit;
  padding: 0;
  position: relative;
  ${legend110}

  &[aria-pressed="true"] {
    cursor: default;
  }

  &[aria-pressed="true"]::after {
    background: currentColor;
    block-size: 1px;
    content: "";
    inset-block-end: 0;
    inset-inline: 0;
    position: absolute;
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 4px;
  }

  ${getMediaQuery("s", { max: true })} {
    font-size: 14px;
    line-height: 20px;
  }
`

const Controls = styled.div`
  align-items: center;
  display: grid;
  gap: 10px;
  grid-template-columns: 24px minmax(0, 1fr) minmax(0, 1fr) 24px;
  padding-inline: 15px;
`

const NavigationButton = styled(IconButton)`
  inline-size: 44px;
  margin-inline: -10px;
`

const NavigationIcon = styled.span`
  display: inline-flex;

  &:dir(rtl) {
    transform: rotate(180deg);
  }
`

const SelectLabel = styled.label`
  min-inline-size: 0;
  position: relative;
`

const ScreenReader = styled.span`
  ${visuallyHidden}
`

const Select = styled.select`
  ${bold}
  appearance: none;
  backdrop-filter: blur(2.5px);
  background: rgb(212 212 212 / 0.3);
  border: 0;
  border-radius: 24px;
  color: inherit;
  cursor: pointer;
  font-family: inherit;
  font-size: 16px;
  block-size: 44px;
  inline-size: 100%;
  padding: 10px 50px 10px 35px;

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }

  option {
    color: initial;
  }

  ${getMediaQuery("s", { max: true })} {
    padding-inline: 20px 42px;
  }
`

const SelectIcon = styled.span`
  inset-block-start: 50%;
  inset-inline-end: 30px;
  pointer-events: none;
  position: absolute;
  transform: translateY(-50%) rotate(90deg);

  &:dir(rtl) {
    transform: translateY(-50%) rotate(-90deg);
  }

  ${getMediaQuery("s", { max: true })} {
    inset-inline-end: 18px;
  }
`

const CalendarTable = styled.table`
  border-collapse: collapse;
  border-spacing: 0;
  inline-size: 100%;
  margin-block-start: 20px;
  table-layout: fixed;
`

const WeekdayRow = styled.tr`
  border-block: 1px solid rgb(var(--pure-white) / 0.3);

  html.prefers-contrast & {
    border-block: 1px solid black;
  }
`

const Weekday = styled.th`
  ${bold}
  block-size: 40px;
  font-size: 14px;
  line-height: 20px;
  padding: 0;
  text-align: center;
`

const DayCell = styled.td`
  block-size: 70px;
  padding: 0;
  text-align: center;
  vertical-align: middle;
`

const CalendarBody = styled.tbody`
  tr:first-of-type ${DayCell} {
    padding-block-start: 10px;
  }
`

const DayButton = styled.button`
  ${light}
  align-items: center;
  appearance: none;
  background: transparent;
  border: 0;
  border-radius: 100px;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  flex-direction: column;
  font-family: inherit;
  font-size: 14px;
  gap: 3px;
  justify-content: center;
  min-block-size: 58px;
  min-inline-size: 44px;
  padding: 5px;

  &[data-today] {
    background: rgb(var(--pure-white) / 0.2);
  }

  &[data-today][data-full-moon] {
    background: rgb(var(--light-black) / 0.2);
  }

  &[data-selected] {
    background: rgb(var(--pure-white));
    color: #031b49;
    cursor: default;
  }

  &[data-selected][data-full-moon] {
    background: rgb(var(--light-black));
    color: rgb(var(--pure-white));
  }

  &[data-full-moon] {
    [data-moon-sprite] {
      filter: drop-shadow(0 0 3px rgb(255 241 216 / 0.95)) drop-shadow(0 0 8px rgb(255 241 216 / 0.7));
    }
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--pure-white));
    outline-offset: 2px;
  }
`

const DayNumber = styled.span`
  line-height: 20px;
`

const Events = styled.div`
  border-block-start: 1px solid rgb(var(--pure-white) / 0.3);
  margin-block-start: 10px;
  html.prefers-contrast & {
    border-block-start: 1px solid black;
  }
`

const EventRow = styled.div`
  align-items: center;
  border-block-end: 1px solid rgb(var(--pure-white) / 0.3);
  display: flex;
  font-size: 16px;
  justify-content: space-between;
  line-height: 22px;
  min-block-size: 64px;
  padding-inline: 25px;

  html.prefers-contrast & {
    border-block-end: 1px solid black;
  }

  ${getMediaQuery("s", { max: true })} {
    align-items: flex-start;
    flex-direction: column;
    justify-content: center;
    padding-inline: 20px;
  }
`

const EventName = styled.span`
  ${bold}
`

const EventDate = styled.time`
  ${light}
  text-align: end;

  ${getMediaQuery("s", { max: true })} {
    text-align: start;
  }
`

function clampMonth(date, minMonth, maxMonth) {
  if (date < minMonth) return minMonth
  if (date > maxMonth) return maxMonth
  return date
}

function getCalendarDate(timestamp, timezone) {
  return DateTime.fromMillis(timestamp, { zone: timezone }).startOf("day")
}

export default function MoonphaseCalendarPopin({
  onSelectDate,
  onClose,
  sprite_cal,
  selectedTime,
  timezone = "local",
  main_title,
  labels,
  close_label,
  ctx,
}) {
  const console = useConsole()
  const dictionary = useDictionary()
  const { closeCalendar, hemisphere, isCalendarOpen, setHemisphere } = useMoonphaseCalendar()
  const { locale } = useMoonCalendar(ctx)
  const dialogId = useId()
  const titleId = useId()
  const tableId = useId()
  const gridRef = useRef(null)
  const shouldRestoreGridFocus = useRef(false)
  const [today, setToday] = useState(() => DateTime.now().setZone(timezone).startOf("day"))
  const [selectedDay, setSelectedDay] = useState(() => getCalendarDate(selectedTime.get(), timezone).toISODate())
  const initialSelectedDay = useRef(selectedDay)
  const hasPendingSelection = useRef(false)
  const selectedDate = DateTime.fromISO(selectedDay, { zone: timezone })
  const pendingSelectedDate = useRef(selectedDate)
  const initialFocusedDate = selectedDate?.isValid ? selectedDate.setZone(timezone).startOf("day") : today
  const [visibleMonth, setVisibleMonth] = useState(() => initialFocusedDate.startOf("month"))
  const [focusedDate, setFocusedDate] = useState(initialFocusedDate)
  const bodyElement = process.browser && document.body
  const closeLabel = close_label || dictionary.popinClose()

  const minYear = today.year - 100
  const maxYear = today.year + 10
  const minMonth = DateTime.fromObject({ year: minYear, month: 1, day: 1 }, { zone: timezone })
  const maxMonth = DateTime.fromObject({ year: maxYear, month: 12, day: 1 }, { zone: timezone })
  const months_fa = Info.months('short', { locale: 'fa-u-ca-persian' })
  const months = locale === "fa" ? months_fa : Info.months("short", { locale })
  const years = Array.from({ length: maxYear - minYear + 1 }, (_, index) => minYear + index)

  const calendar = useMemo(() => {
    const selection = selectedDate?.isValid ? selectedDate.setZone(timezone).startOf("day") : null

    return buildMonthCalendar({ visibleMonth, today, selectedDate: selection, locale })
  }, [visibleMonth, today, selectedDay, timezone, locale])

  const dateFormatter = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: today.zoneName,
  })

  const eventFormatter = new Intl.DateTimeFormat(locale, locale === 'fa' ? {
    year: "numeric",
    month: "short",
    day: "2-digit",
    calendar: 'persian',
  } : {
    dateStyle: 'full',
  })
  /*
    function formatDate(tm) {
      return new Intl.DateTimeFormat(locale, {
        year: "numeric",
        month: "long",
        weekday: "long",
      }).format(tm)
    }
  */
  const illuminationFormatter = new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 0 })
  useLayoutEffect(() => {
    if (!isCalendarOpen) return

    const mainDate = getCalendarDate(selectedTime.get(), timezone)
    initialSelectedDay.current = mainDate.toISODate()
    pendingSelectedDate.current = mainDate
    hasPendingSelection.current = false
    setSelectedDay(initialSelectedDay.current)
  }, [isCalendarOpen, selectedTime, timezone])

  useEffect(() => {
    setToday(DateTime.now().setZone(timezone).startOf("day"))
  }, [timezone])

  useEffect(() => {
    if (!selectedDate?.isValid) return
    const nextSelection = selectedDate.setZone(timezone).startOf("day")
    const nextMonth = nextSelection.startOf("month")
    const minBound = DateTime.fromObject({ year: minYear, month: 1, day: 1 }, { zone: timezone })
    const maxBound = DateTime.fromObject({ year: maxYear, month: 12, day: 1 }, { zone: timezone })
    setFocusedDate(nextSelection)
    setVisibleMonth(clampMonth(nextMonth, minBound, maxBound))
  }, [selectedDay, timezone, minYear, maxYear])

  useEffect(() => {
    if (!shouldRestoreGridFocus.current) return
    shouldRestoreGridFocus.current = false
    gridRef.current?.querySelector(`[data-date="${focusedDate.toISODate()}"]`)?.focus()
  }, [focusedDate, visibleMonth])

  const selectDate = cell => {
    pendingSelectedDate.current = cell.date
    onSelectDate?.(pendingSelectedDate.current, cell.isFullMoon)
    hasPendingSelection.current = cell.iso !== initialSelectedDay.current
    setSelectedDay(cell.iso)
    setFocusedDate(cell.date)
  }

  const handleCloseCalendar = () => {
    //    if (hasPendingSelection.current) onSelectDate?.(pendingSelectedDate.current)
    closeCalendar()
    onClose?.()
  }

  const setMonth = nextMonth => {
    const next = clampMonth(nextMonth.startOf("month"), minMonth, maxMonth)
    const nextFocusedDay = Math.min(focusedDate.day, next.daysInMonth)
    setVisibleMonth(next)
    setFocusedDate(next.set({ day: nextFocusedDay }))
  }

  const moveGridFocus = nextDate => {
    if (nextDate < minMonth || nextDate > maxMonth.endOf("month")) return
    shouldRestoreGridFocus.current = true
    setFocusedDate(nextDate)
    if (!nextDate.hasSame(visibleMonth, "month")) setVisibleMonth(nextDate.startOf("month"))
  }

  const onDayKeyDown = (event, cell) => {
    let nextDate = null

    switch (event.key) {
      case "ArrowLeft":
        nextDate = cell.date.minus({ days: 1 })
        break
      case "ArrowRight":
        nextDate = cell.date.plus({ days: 1 })
        break
      case "ArrowUp":
        nextDate = cell.date.minus({ days: 7 })
        break
      case "ArrowDown":
        nextDate = cell.date.plus({ days: 7 })
        break
      case "Home":
        nextDate = cell.date.minus({ days: (cell.date.weekday - calendar.weekStart + 7) % 7 })
        break
      case "End":
        nextDate = cell.date.plus({ days: (calendar.weekStart + 6 - cell.date.weekday + 7) % 7 })
        break
      case "PageUp":
        nextDate = event.shiftKey ? cell.date.minus({ years: 1 }) : cell.date.minus({ months: 1 })
        break
      case "PageDown":
        nextDate = event.shiftKey ? cell.date.plus({ years: 1 }) : cell.date.plus({ months: 1 })
        break
      default:
        return
    }

    event.preventDefault()
    moveGridFocus(nextDate)
  }

  const isPreviousDisabled = visibleMonth <= minMonth
  const isNextDisabled = visibleMonth >= maxMonth

  return (
    <>
      {bodyElement &&
        createPortal(
          <AnimatePresence>
            {isCalendarOpen && (
              <CalendarModal
                className={moonphaseModal}
                id={dialogId}
                modalHeading={titleId}
                onClose={handleCloseCalendar}
                scrollTarget='container'
                showCloseButton={false}
              >
                <Panel aria-labelledby={titleId}>
                  <Header>
                    <Title id={titleId}>{main_title}</Title>
                    <CloseButton type='button' onClick={handleCloseCalendar} aria-label={closeLabel}>
                      <Icon type='close' size={14} />
                    </CloseButton>
                  </Header>

                  <HemisphereControls role='group' aria-label={labels.hemisphere ?? "Hemisphere"}>
                    <HemisphereButton type='button' aria-pressed={hemisphere === "north"} onClick={() => setHemisphere("north")}>
                      {labels.northern_hemisphere ?? "Northern hemisphere"}
                    </HemisphereButton>
                    <HemisphereButton type='button' aria-pressed={hemisphere === "south"} onClick={() => setHemisphere("south")}>
                      {labels.southern_hemisphere ?? "Southern hemisphere"}
                    </HemisphereButton>
                  </HemisphereControls>

                  <Controls>
                    <NavigationButton
                      type='button'
                      onClick={() => setMonth(visibleMonth.minus({ months: 1 }))}
                      aria-label={labels.previous_month}
                      disabled={isPreviousDisabled}
                    >
                      <NavigationIcon>
                        <Icon type='movePrevious' size={14} />
                      </NavigationIcon>
                    </NavigationButton>

                    <SelectLabel>
                      <ScreenReader>{labels.month}</ScreenReader>
                      <Select
                        value={visibleMonth.month}
                        onChange={event => setMonth(visibleMonth.set({ month: Number(event.target.value), day: 1 }))}
                        aria-label={labels.month}
                      >
                        {months.map((month, index) => (
                          <option value={index + 1} key={month}>
                            {month.replace(/Tháng|Thang/i, 'Thg')}
                          </option>
                        ))}
                      </Select>
                      <SelectIcon aria-hidden='true'>
                        <Icon type='chevron' size={14} />
                      </SelectIcon>
                    </SelectLabel>

                    <SelectLabel>
                      <ScreenReader>{labels.year}</ScreenReader>
                      <Select
                        value={visibleMonth.year}
                        onChange={event => setMonth(visibleMonth.set({ year: Number(event.target.value), day: 1 }))}
                        aria-label={labels.year}
                      >
                        {years.map(year => (
                          <option value={year} key={year}>
                            {convertToIndianNumerals(DateTime.fromObject({ year }).setLocale(locale).toLocaleString({ year: 'numeric' }), "ar", locale)}
                          </option>
                        ))}
                      </Select>
                      <SelectIcon aria-hidden='true'>
                        <Icon type='chevron' size={14} />
                      </SelectIcon>
                    </SelectLabel>

                    <NavigationButton
                      type='button'
                      onClick={() => setMonth(visibleMonth.plus({ months: 1 }))}
                      aria-label={labels.next_month}
                      disabled={isNextDisabled}
                    >
                      <NavigationIcon>
                        <Icon type='moveNext' size={14} />
                      </NavigationIcon>
                    </NavigationButton>
                  </Controls>

                  <CalendarTable ref={gridRef} id={tableId} aria-label={`${months[visibleMonth.month - 1]} ${visibleMonth.year}`}>
                    <thead>
                      <WeekdayRow>
                        {calendar.weekdays.map((weekday, index) => (
                          <Weekday scope='col' key={`${weekday}-${index}`}>
                            {weekday}
                          </Weekday>
                        ))}
                      </WeekdayRow>
                    </thead>
                    <CalendarBody>
                      {calendar.weeks.map(week => (
                        <tr key={week[0].iso}>
                          {week.map(cell => (
                            <DayCell key={cell.iso} aria-hidden={!cell.isCurrentMonth || undefined}>
                              {cell.isCurrentMonth && (
                                <DayButton
                                  type='button'
                                  data-date={cell.iso}
                                  data-selected={cell.isSelected || undefined}
                                  data-today={cell.isToday || undefined}
                                  data-full-moon={cell.isFullMoon || undefined}
                                  aria-current={cell.isToday ? "date" : undefined}
                                  aria-pressed={cell.isSelected}
                                  aria-selected={cell.isSelected}
                                  aria-label={`${dateFormatter.format(cell.date.toJSDate())}, ${illuminationFormatter.format(cell.phase.illumination)}`}
                                  tabIndex={cell.date.hasSame(focusedDate, "day") ? 0 : -1}
                                  onClick={() => selectDate(cell)}
                                  onFocus={() => setFocusedDate(cell.date)}
                                  onKeyDown={event => onDayKeyDown(event, cell)}
                                >
                                  <DayNumber aria-hidden='true'>{convertToIndianNumerals(DateTime.fromObject({ year: 2026, month: 1, day: cell.dayNumber }).setLocale(locale).toLocaleString({ day: 'numeric' }), "ar", locale)}</DayNumber>
                                  <MoonSprite sprite_cal={sprite_cal} hemisphere={hemisphere} progress={cell.phase.progress} cell={cell} size={40} />
                                </DayButton>
                              )}
                            </DayCell>
                          ))}
                        </tr>
                      ))}
                    </CalendarBody>
                  </CalendarTable>

                  <Events>
                    <EventRow>
                      <EventName>{labels.latest_full_moon}</EventName>
                      <EventDate dateTime={calendar.latestFullMoonDate.toString().slice(0, 10)}>
                        {eventFormatter.format(calendar.latestFullMoonDate.toMillis())}
                      </EventDate>
                    </EventRow>
                  </Events>

                  <MoonphaseInstructions labels={labels} locale={locale} timezone={timezone} />
                </Panel>
              </CalendarModal>
            )}
          </AnimatePresence>,
          bodyElement
        )}
    </>
  )
}
